import {
  gestifyCreateDfveDocumento,
  gestifyCreateMultiDfveRiga,
  gestifyFetchAnagrafica,
  gestifyFetchSezionali,
  gestifyLogin,
  gestifyPrenotaDfveDocumento,
} from '../../api';
import {
  DFVE,
  DFVE_FATT,
  DocumentoRigaCreateType,
  Sezionale,
} from '../../models';
import { i18nConfig } from '@jobcost-monorepo/i18n';
import { handleGlobalError } from '@jobcost-monorepo/crud';
import dayjs from '@jobcost-monorepo/dayjs-config';

// Sezionale usato se in Gestify non ne è impostato uno predefinito per DFVE
const SEZIONALE_FALLBACK_ID = 1;

interface GestifyInvoiceProps {
  username: string;
  password: string;
  externalId: number;
  dataDoc: string;
  righe: DocumentoRigaCreateType[];
}

interface GestifyInvoiceReturn {
  docId: number;
  docNumber: string;
}

export const createGestifyInvoice = async ({
  username,
  password,
  externalId,
  dataDoc,
  righe,
}: GestifyInvoiceProps): Promise<GestifyInvoiceReturn | undefined> => {
  console.log({ dataDoc });
  try {
    // Effettua il login
    const loginResponse = await gestifyLogin({
      username,
      password,
    });
    if (!loginResponse || loginResponse.errors) {
      handleGlobalError({
        code: 999,
        message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
        description: i18nConfig.t('invoice.errors.loginError', {
          ns: 'timesheets',
        }),
        responseMessage: i18nConfig.t('invoice.errors.loginError', {
          ns: 'timesheets',
        }),
      });
      return;
    }

    // Recupera i dati relativi all'anagrafica associata
    const anagraficaResponse = await gestifyFetchAnagrafica({
      id: externalId,
      edit_mode: false,
    });

    if (!anagraficaResponse || anagraficaResponse.errors) {
      handleGlobalError({
        code: 999,
        message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
        description: i18nConfig.t('invoice.errors.customerError', {
          ns: 'timesheets',
        }),
        responseMessage: i18nConfig.t('invoice.errors.customerError', {
          ns: 'timesheets',
        }),
      });
      return;
    }

    // Recupera i dati relativi ai sezionali
    const sezionaliResponse = await gestifyFetchSezionali({
      attivo: true,
      documento_tipo_predefiniti: DFVE,
    });

    if (!sezionaliResponse || sezionaliResponse.errors) {
      handleGlobalError({
        code: 999,
        message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
        description: i18nConfig.t('invoice.errors.invoiceCodeError', {
          ns: 'timesheets',
        }),
        responseMessage: i18nConfig.t('invoice.errors.invoiceCodeError', {
          ns: 'timesheets',
        }),
      });
      return;
    }

    // Usa il sezionale predefinito per DFVE, altrimenti quello con id 1 (se attivo)
    const sezionaleId: number | undefined =
      sezionaliResponse?.data?.predefiniti?.[0]?.sezionale_id ??
      sezionaliResponse?.data?.data?.find(
        (s: Sezionale) => s.id === SEZIONALE_FALLBACK_ID,
      )?.id;

    if (!sezionaleId) {
      handleGlobalError({
        code: 999,
        message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
        description: i18nConfig.t('invoice.errors.noSezionaleError', {
          ns: 'timesheets',
        }),
        responseMessage: i18nConfig.t('invoice.errors.noSezionaleError', {
          ns: 'timesheets',
        }),
      });
      return;
    }

    // Calcola il progressivo della fattura
    console.log('dataDoc', dataDoc);
    const prenotaResponse = await gestifyPrenotaDfveDocumento({
      documento: DFVE,
      sezionale_id: sezionaleId,
      data: dayjs(dataDoc).toDate(),
    });

    console.log('prenotaResponse', prenotaResponse);

    if (!prenotaResponse || prenotaResponse.errors) {
      handleGlobalError({
        code: 999,
        message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
        description: i18nConfig.t('invoice.errors.invoiceNumberError', {
          ns: 'timesheets',
        }),
        responseMessage: i18nConfig.t('invoice.errors.invoiceNumberError', {
          ns: 'timesheets',
        }),
      });
      return;
    }

    // Crea la fattura
    const progressivo = prenotaResponse?.data?.numero ?? 0;

    console.log('progressivo', progressivo);
    console.log('gestifyCreateDfveDocumento');
    const documentoResponse = await gestifyCreateDfveDocumento({
      id: 0,
      tipo: DFVE,
      tipo_sub: DFVE_FATT,
      anagrafica_id: anagraficaResponse.data.id,
      sezionale: { id: String(sezionaleId) },
      numero: progressivo,
      data_operazione: dayjs(dataDoc).toDate(),
    });
    console.log('documentoResponse', documentoResponse);

    if (!documentoResponse || documentoResponse.errors) {
      handleGlobalError({
        code: 999,
        message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
        description: i18nConfig.t('invoice.errors.invoiceHeaderError', {
          ns: 'timesheets',
        }),
        responseMessage: i18nConfig.t('invoice.errors.invoiceHeaderError', {
          ns: 'timesheets',
        }),
      });
      return;
    }

    // Crea le righe della fattura
    const ivaPredefinita =
      documentoResponse?.data?.data?.extra_info?.predefiniti?.iva;
    const umPredefinita =
      documentoResponse?.data?.data?.extra_info?.predefiniti?.unita_misura;

    const createRigheResponse = await gestifyCreateMultiDfveRiga({
      parentId: documentoResponse.data.data.id,
      documento_righe: righe.map((riga) =>
        riga.quantita
          ? {
              ...riga,
              unita_misura: { id: String(umPredefinita.id) },
              iva: { id: String(ivaPredefinita.id) },
            }
          : riga,
      ),
    });
    console.log('gestifyCreateMultiDfveRiga', { createRigheResponse });
    if (!createRigheResponse || createRigheResponse.errors) {
      handleGlobalError({
        code: 999,
        message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
        description: i18nConfig.t('invoice.errors.invoiceBodyError', {
          ns: 'timesheets',
        }),
        responseMessage: i18nConfig.t('invoice.errors.invoiceBodyError', {
          ns: 'timesheets',
        }),
      });
      return;
    }

    let docNumber = documentoResponse.data?.data?.numero;
    if (documentoResponse.data?.data?.sezionale) {
      docNumber = `${docNumber}/${documentoResponse.data?.data?.sezionale.descrizione}`;
    }

    console.log({
      docId: documentoResponse.data?.data?.id,
      docNumber,
    });
    return {
      docId: documentoResponse.data?.data?.id,
      docNumber,
    };
  } catch (e) {
    console.log(e);
    handleGlobalError({
      code: 999,
      message: i18nConfig.t('invoice.title', { ns: 'timesheets' }),
      description: i18nConfig.t('invoice.errors.invoiceGenericError', {
        ns: 'timesheets',
      }),
      responseMessage: i18nConfig.t('invoice.errors.invoiceGenericError', {
        ns: 'timesheets',
      }),
    });
  }
};
